import { ReactComponent as DeleteIcon } from 'assett/icons/delete_outline.svg';
import { FC } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store/store';
import { IconButton, Select, TableRow, Typography } from 'styles';
import SelectItem from 'styles/select/SelectItem';
import palette from 'styles/theme/Palette';
import { theme } from 'styles/theme/ThemeStyle';
import { IClaim, IClaimComponents } from 'types/Claim';
import { useTranslation } from 'utils/translations';
import { formatInputNumberValue } from 'utils/utils';
import { OutlinedInput, TableCell } from './PriceEstimate';

type OtherReceiptItemRowProps = {
	tempAmounts: IClaim['amount'];
	row: IClaimComponents;
	removeComponent: (rowNumber: number) => void;
	handleChangeServiceName: (rowNumber: number, serviceId: number) => void;
	error: {
		grossAmount: boolean;
		maxValue: boolean;
		retailNotTaxAndNet: boolean;
		taxBiggerNet: boolean;
	};
	checkIsError: (
		row: IClaimComponents,
		forceChecks?: boolean,
	) => {
		grossAmount: boolean;
		maxValue: boolean;
		retailNotTaxAndNet: boolean;
		taxBiggerNet: boolean;
	};
	handleChangeAmounts: (
		formattedValue: string,
		serviceId: number | null,
		key: string,
	) => void;
	isOtherComponent: (row: IClaimComponents) => boolean;
	addCurrency: (value: number | string | undefined) => string;
	rowNumber: number;
	showTaxColumns: boolean;
	status: 'beforeCalculation' | 'afterCalculation' | 'onEdit';
};

const OtherReceiptItemRow: FC<OtherReceiptItemRowProps> = ({
	tempAmounts,
	row,
	removeComponent,
	handleChangeServiceName,
	checkIsError,
	handleChangeAmounts,
	isOtherComponent,
	addCurrency,
	rowNumber,
	showTaxColumns,
	status,
}) => {
	const { t: tClientLabels } = useTranslation('', {
		keyPrefix: 'clientLabels',
	});
	const { otherServices } = useSelector((state: RootState) => state.claim);

	const isServiceSelected = (serviceId: number) => {
		return !tempAmounts.components
			.filter(el => isOtherComponent(el))
			.map(otherEl => otherEl.serviceId)
			.includes(serviceId);
	};

	return (
		<TableRow
			sx={{
				height: { sm: 56, lg: 70 },
				backgroundColor: theme.palette.white.main,
				outline: `2px solid ${theme.palette.grey6.main}`,
			}}
		>
			<TableCell sx={{ width: { sm: 136, lg: 230 }, pt: 0 }}>
				{['beforeCalculation', 'onEdit'].includes(status) ? (
					<Select
						value={row.serviceId ? row.serviceId : ''}
						onChange={value => {
							handleChangeServiceName(rowNumber, value.target.value as number);
						}}
						sx={{
							height: '34px',
							width: '160px',
							fontSize: '13px',
							fontWeight: 600,
							textAlign: 'left',
							ml: '10px',

							'& .MuiSelect-icon': {
								top: 'auto',
							},
						}}
						MenuProps={{
							autoFocus: false,
							MenuListProps: {
								sx: {
									position: 'relative',
									display: 'flex',
									flexDirection: 'column',
									justifyItems: 'center',
									'& .MuiButtonBase-root.MuiMenuItem-root.Mui-selected, .Mui-selected:hover':
										{
											backgroundColor: theme => theme.palette.grey6.main,
										},
								},
							},
							PaperProps: {
								sx: {
									paddingTop: 0,
									paddingBottom: 0,
								},
							},
						}}
					>
						{otherServices?.map((e, index) =>
							e.serviceId ? (
								<SelectItem
									value={e.serviceId}
									sx={{
										height: 34,
										pl: '10px',
										pr: '10px',
										borderTop: index >= 1 ? '1px solid' : 'none',
										borderColor: theme.palette.grey4.main,
									}}
									variant="checkbox"
									disabled={!isServiceSelected(e.serviceId)}
									key={`day_${index}`}
								>
									{tClientLabels(
										`newClaim.priceEstimate.otherReceiptItems.${e.serviceLabel}`,
									)}
								</SelectItem>
							) : (
								<></>
							),
						)}
					</Select>
				) : (
					<Typography variant="body" sx={{ pl: '10px' }}>
						{tClientLabels(
							`newClaim.priceEstimate.otherReceiptItems.${
								otherServices.find(el => el.serviceId === row.serviceId)?.serviceLabel
							}`,
						)}
					</Typography>
				)}
			</TableCell>
			<TableCell align="center" sx={{ width: { sm: 136, lg: 230 } }}>
				{['beforeCalculation', 'onEdit'].includes(status) ? (
					<OutlinedInput
						error={checkIsError(row).grossAmount || checkIsError(row).maxValue}
						isCurrency={true}
						value={formatInputNumberValue(row.grossRetail || '')}
						handleInputNumberChange={formattedValue => {
							handleChangeAmounts(formattedValue, row.serviceId, 'grossRetail');
						}}
						disabled={row.serviceId === null}
					/>
				) : (
					<Typography variant="value1">{addCurrency(row.grossRetail)}</Typography>
				)}
			</TableCell>
			<TableCell align="center" $smallIcon></TableCell>
			<TableCell align="center">
				{!['beforeCalculation', 'onEdit'].includes(status) && (
					<Typography variant="value1">
						{addCurrency(row.inStorePromotion)}
					</Typography>
				)}
			</TableCell>
			<TableCell align="center" $smallIcon></TableCell>
			<TableCell align="center" sx={{ width: { sm: 136, lg: 230 } }}>
				{['beforeCalculation', 'onEdit'].includes(status) ? (
					<OutlinedInput
						error={
							checkIsError(row).grossAmount || checkIsError(row).retailNotTaxAndNet
						}
						isCurrency={true}
						value={formatInputNumberValue(row.retailAmount || '')}
						handleInputNumberChange={formattedValue => {
							handleChangeAmounts(formattedValue, row.serviceId, 'retailAmount');
						}}
						disabled={row.serviceId === null}
					/>
				) : (
					<Typography variant="value1">{addCurrency(row.retailAmount)}</Typography>
				)}
			</TableCell>
			<TableCell align="center" $smallIcon></TableCell>
			{showTaxColumns ? (
				<>
					<TableCell align="center" sx={{ width: { sm: 136, lg: 230 } }}>
						{['onEdit'].includes(status) ? (
							<OutlinedInput
								error={
									checkIsError(row).retailNotTaxAndNet || checkIsError(row).taxBiggerNet
								}
								isCurrency={true}
								value={formatInputNumberValue(row.retailNetOfTax || '0')}
								handleInputNumberChange={formattedValue => {
									handleChangeAmounts(formattedValue, row.serviceId, 'retailNetOfTax');
								}}
								disabled={row.serviceId === null}
							/>
						) : (
							<Typography variant="value1">
								{addCurrency(row.retailNetOfTax)}
							</Typography>
						)}
					</TableCell>
					<TableCell align="center" $smallIcon></TableCell>
					<TableCell align="center" sx={{ width: { sm: 136, lg: 230 } }}>
						{['onEdit'].includes(status) ? (
							<OutlinedInput
								error={
									checkIsError(row).retailNotTaxAndNet || checkIsError(row).taxBiggerNet
								}
								isCurrency={true}
								value={formatInputNumberValue(row.tax || '0')}
								handleInputNumberChange={formattedValue => {
									handleChangeAmounts(formattedValue, row.serviceId, 'tax');
								}}
								disabled={row.serviceId === null}
							/>
						) : (
							<Typography variant="value1">{addCurrency(row.tax)}</Typography>
						)}
					</TableCell>
					<TableCell align="center" $smallIcon></TableCell>
				</>
			) : (
				<></>
			)}
			<TableCell align="center">
				{!['beforeCalculation', 'onEdit'].includes(status) && (
					<Typography variant="value1">
						{addCurrency(row.contractualAdjustmentAmount)}
					</Typography>
				)}
			</TableCell>
			<TableCell align="center" $smallIcon>
				{!['beforeCalculation', 'onEdit'].includes(status) && (
					<Typography variant="value1" style={{ color: palette.grey2.main }}>
						{addCurrency(row.discountedAmount)}
					</Typography>
				)}
			</TableCell>
			<TableCell align="center">
				{!['beforeCalculation', 'onEdit'].includes(status) && (
					<Typography variant="value1">
						{addCurrency(row.memberPayAmount)}
					</Typography>
				)}
			</TableCell>
			<TableCell align="center" $smallIcon></TableCell>
			<TableCell align="center">
				{!['beforeCalculation', 'onEdit'].includes(status) && (
					<Typography variant="value1">
						{addCurrency(row.allowablePlanPay)}
					</Typography>
				)}
			</TableCell>
			{['beforeCalculation', 'onEdit'].includes(status) ? (
				<TableCell
					align="center"
					sx={{
						backgroundColor: theme => theme.palette.grey6.main,
						position: 'sticky',
						right: 0,
						width: 44,
					}}
				>
					<IconButton onClick={() => removeComponent(rowNumber)}>
						<DeleteIcon />
					</IconButton>
				</TableCell>
			) : null}
		</TableRow>
	);
};

export default OtherReceiptItemRow;
